import React from 'react'
import { HexColorPicker } from 'react-colorful'
import { css } from 'styled-components'

import { FieldMetadata, useFieldMetadata } from '../FieldMetadata'
import { FormInputProps, FormMaskInput } from '../FormInput'
import { Popover, PopoverTrigger } from '../Popover'
import { PopoverContent } from '../Popover/Popover'

export interface ColorPickerInputProps
  extends Omit<FormInputProps, 'onChange'> {
  value: string
  style?: React.CSSProperties
  onChange?: (value: string) => void
  disabled?: boolean
}

function ColorPickerInput(
  {
    onChange,
    label,
    errorMessage,
    description,
    style,
    ...props
  }: ColorPickerInputProps,
  ref: React.Ref<HTMLInputElement>
) {
  const { fieldProps, ...fieldMetadataProps } = useFieldMetadata({
    ...props,
    label,
    errorMessage,
    description,
  })
  return (
    <FieldMetadata {...fieldMetadataProps} style={style}>
      <div
        css={css`
          display: flex;
          flex-direction: row;

          [data-base-input-outline] {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
            border-left-width: 0;
          }
        `}
        data-testid='color-picker-input-container'
      >
        <Popover>
          <PopoverTrigger
            asChild={true}
            data-testid='color-picker-popover-trigger-container'
          >
            <div
              css={css`
                border-radius: var(--border-radius-md);
                border-top-right-radius: 0;
                border-bottom-right-radius: 0;
                border: 1px solid var(--gray200);
                width: 50px;
                background-color: ${props.value};
                cursor: pointer;
              `}
              data-testid='color-picker-popover-trigger'
            />
          </PopoverTrigger>
          <PopoverContent data-testid='color-picker-popover-content'>
            <HexColorPicker
              color={props.value}
              onChange={(val) => {
                onChange?.(val && val !== '#NaNNaNNaN' ? val : '#000000')
              }}
              data-testid='color-picker'
            />
          </PopoverContent>
        </Popover>
        <div css={css`
          width: 100%;
        `}>
          <FormMaskInput
            // Since on clear it sends #______, we send "" instead
            onChange={(val) => onChange?.(val === '#______' ? '' : val)}
            mask='#******'
            {...props}
            {...fieldProps}
            data-testid='color-picker-form-mask-input'
          />
        </div>
      </div>
    </FieldMetadata>
  )
}
export default React.forwardRef(ColorPickerInput)
